"use client";

import React from "react";
import { Clock, Mail, Phone, CircleCheck, Loader, ArrowRight, MapPin } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const SERVICE_OPTIONS = ["Gülüş Tasarımı (Hollywood Smile)", "İmplant Tedavisi", "Diş Beyazlatma (Bleaching)", "Zirkonyum & Porselen Kaplama", "Ortodonti (Şeffaf Plak)", "Diş Eti Tedavileri", "Pedodonti (Çocuk Diş)", "Diğer"];
const inputClasses = "border-input placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 flex h-10 w-full rounded-sm border bg-transparent px-3 py-2 text-base shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 md:text-sm";
function RequestQuoteForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="85033" className="flex flex-col items-center justify-center space-y-4 rounded-sm border border-border bg-card p-8 text-center shadow-sm">
        <div className="flex size-16 items-center justify-center rounded-full bg-primary/10 text-primary">
          <CircleCheck className="size-8" />
        </div>
        <h3 className="text-2xl font-serif font-semibold text-card-foreground">Randevu Talebiniz Alındı</h3>
        <p className="text-muted-foreground">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4 rounded-sm border-primary text-primary hover:bg-primary/5">
          Yeni Bir Randevu Formu Doldur
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6 rounded-sm border border-border bg-card p-6 shadow-md md:p-8">
      <div className="grid gap-6 md:grid-cols-2">
        <FormField control={control} name="name" rules={{
        required: "Adınız Soyadınız zorunludur."
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-medium">Adınız Soyadınız</FormLabel>
              <FormControl>
                <input type="text" className={inputClasses} placeholder="Örneğin: Ayşe Yılmaz" {...field} />
              </FormControl>
              <FormDescription className="text-xs">Size en iyi şekilde hitap edebilmemiz için tam adınızı giriniz.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="company" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-medium">Tercih Edilen Tarih ve Saat <span className="text-muted-foreground font-normal text-xs">(Opsiyonel)</span></FormLabel>
              <FormControl>
                <input type="text" className={inputClasses} placeholder="Örneğin: Gelecek Salı Öğleden Sonra" {...field} />
              </FormControl>
              <FormDescription className="text-xs">Size en uygun zaman dilimini belirtebilirsiniz.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "E-posta adresi zorunludur.",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Geçerli bir e-posta adresi giriniz."
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-medium">E-posta Adresi</FormLabel>
              <FormControl>
                <input type="email" className={inputClasses} placeholder="ayse@example.com" {...field} />
              </FormControl>
              <FormDescription className="text-xs">Randevu detaylarını iletebilmemiz için geçerli bir e-posta.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Telefon numarası zorunludur.",
        pattern: {
          value: /^[0-9+\-\s()]*$/,
          message: "Geçerli bir telefon numarası giriniz."
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-medium">Telefon Numarası</FormLabel>
              <FormControl>
                <input type="tel" className={inputClasses} placeholder="+90 5xx xxx xx xx" {...field} />
              </FormControl>
              <FormDescription className="text-xs">Size en kısa sürede geri dönüş yapabilmemiz için.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="service_interest" rules={{
      required: "Lütfen ilgilendiğiniz tedaviyi seçiniz."
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium">İlgilendiğiniz Tedavi</FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger className="rounded-sm border-input">
                  <SelectValue placeholder="Size nasıl yardımcı olabiliriz?" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {SERVICE_OPTIONS.map((option, i) => <SelectItem key={i} value={option} data-index={i}>
                    {option}
                  </SelectItem>)}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="details" rules={{
      required: "Mesajınız zorunludur."
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium">Mesajınız veya Sorularınız</FormLabel>
            <FormControl>
              <Textarea placeholder="Tedavi geçmişiniz veya sormak istediğiniz detayları buraya yazabilirsiniz..." className="min-h-[120px] resize-y rounded-sm border-input focus-visible:ring-ring/50" {...field} />
            </FormControl>
            <FormDescription className="text-xs">Eklemek istediğiniz özel bir notunuz varsa belirtebilirsiniz.</FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="rounded-sm bg-destructive/10 p-4 text-sm text-destructive">
          {submitError}
        </div>}

      <Button type="submit" size="lg" disabled={isSubmitting} className="w-full sm:w-auto rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 transition-colors shadow-sm">
        {isSubmitting ? <>
            <Loader className="mr-2 size-5 animate-spin" />
            Gönderiliyor...
          </> : <>
            Randevu Talebi Gönder <ArrowRight className="ml-2 size-4" />
          </>}
      </Button>
    </Form>;
}
export default function TalepVeTeklifFormu() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="online-randevu-talebi" className="relative overflow-hidden bg-background py-16 md:py-24 text-foreground" ref={sectionRef}>
      <div className="pointer-events-none absolute top-0 right-0 -translate-y-1/4 translate-x-1/4 opacity-10">
        <div className="size-[600px] rounded-full bg-primary/20 blur-[120px]" />
      </div>
      <div className="pointer-events-none absolute bottom-0 left-0 translate-y-1/4 -translate-x-1/4 opacity-10">
        <div className="size-[400px] rounded-full bg-secondary/40 blur-[100px]" />
      </div>

      <div className="container relative mx-auto px-4 md:px-6">
        <div className="grid gap-12 lg:grid-cols-[1fr_1.2fr] lg:gap-16">
          
          <div className={`flex flex-col justify-center space-y-8 transition-all duration-1000 ${isVisible ? "translate-y-0 opacity-100" : "translate-y-12 opacity-0"}`}>
            <div className="space-y-4">
              <Badge variant="secondary" className="px-3 py-1 text-xs font-medium bg-secondary text-primary border-none rounded-sm tracking-wider uppercase">
                <Clock className="mr-1.5 size-4 text-primary" />
                Hızlı Geri Dönüş Garantisi
              </Badge>
              <h2 className="text-3xl font-serif font-normal tracking-tight text-foreground sm:text-4xl md:text-5xl leading-tight">Diş Hekimi<br />Ayşegül İNNECİ UZUN&nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp;&nbsp;<span className="text-primary italic" data-ast-id="f:/src/pages/Contact/sections/TalepVeTeklifFormu.tsx|sig:55x7uv" data-ast-start="9026" data-ast-end="9203">Randevu Alın</span></h2>
              <p className="text-lg text-muted-foreground leading-relaxed font-sans">Kayseri'de implant ve estetik diş hekimliği alanında butik ve lüks bir deneyim sunuyoruz. Hastalarımızın memnuniyetini her zaman ön planda tutuyoruz. Formu doldurarak hayalinizdeki gülüşe giden ilk adımı güvenle atabilirsiniz.</p>
            </div>

            <div className="space-y-6 rounded-sm border border-border bg-card/40 p-6 backdrop-blur-md shadow-sm">
              <h3 className="text-xl font-serif font-medium text-card-foreground">İletişim Bilgilerimiz</h3>
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-1">
                <div className="flex items-center space-x-4">
                  <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/5 text-primary">
                    <Phone className="size-5" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Telefon / WhatsApp</p>
                    <p className="text-base font-semibold text-foreground">+90 553 552 32 34</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/5 text-primary">
                    <Mail className="size-5" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">E-posta</p>
                    <p className="text-base font-semibold text-foreground">dt.aysegulinneci@gmail.com</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/5 text-primary">
                    <MapPin className="size-5" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Adres</p>
                    <p className="text-sm font-semibold text-foreground leading-tight">Tacettin Veli, Lalezade Cd. Kartal İş Merkezi No:45 Kat:2 No:6, Melikgazi/Kayseri</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/5 text-primary">
                    <Clock className="size-5" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Çalışma Saatleri</p>
                    <p className="text-sm font-semibold text-foreground">Pzt-Cmt: 08:30 - 18:00</p>
                  </div>
                </div>
              </div>

              <div className="mt-4 overflow-hidden rounded-sm border border-border">
                <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3113.195222534767!2d35.49215277722817!3d38.713326657542154!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x152b138231641887%3A0x9d265367afddc998!2zRGnFnyBIZWtpbWkgQXnFn2Vnw7xsIMSwTk5FQ8SwIFVaVU4txLBtcGxhbnRvbG9qaSB2ZSBFc3RldGlrIERpxZ8gS2xpbmnEn2kgLyBLQVlTRVLEsA!5e0!3m2!1str!2str!4v1775308147801!5m2!1str!2str" width="100%" height="220" style={{
                border: 0
              }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Klinik Konumu" className="grayscale hover:grayscale-0 transition-all duration-500" />
              </div>
            </div>
          </div>

          <div className={`transition-all delay-300 duration-1000 ${isVisible ? "translate-y-0 opacity-100" : "translate-y-12 opacity-0"}`}>
            <FormProvider formKey="appointment_form_contact" formId="appointment_form_contact" sectionName="online-randevu-talebi" defaultValues={{
            name: "",
            email: "",
            phone: "",
            company: "",
            service_interest: "",
            details: ""
          }} submitText="Randevu Talebi Gönder" successMessage="Randevu talebiniz başarıyla alındı. Hasta koordinatörümüz size en özel deneyimi sunmak için kısa süre içinde iletişime geçecektir." errorMessage="Form gönderilirken bir hata oluştu. Lütfen bilgilerinizi kontrol edip tekrar deneyiniz.">
              <RequestQuoteForm />
            </FormProvider>
          </div>

        </div>
      </div>
    </section>;
}